import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { MultiSelectTags } from '@/components/ui/multi-select-tags';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { profileApi } from '@/lib/api/profile';
import { codebookApi } from '@/lib/api/codebook';
import { UpdateExpertisesDto } from '@paladin-monorepo/common';
import { SuccessIndicator } from '@/components/ui/success-indicator';

export const ExpertisesCard = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showSuccess, setShowSuccess] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);

  const [expertiseIds, setExpertiseIds] = useState<string[]>([]);
  const [primaryIds, setPrimaryIds] = useState<string[]>([]);
  const [customLabelById, setCustomLabelById] = useState<Record<string, string>>({});

  const { data: profile, isLoading: loadingProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileApi.getProfile(),
  });

  const { data: options = [], isLoading: loadingOptions } = useQuery({
    queryKey: ['codebook', 'expertises'],
    queryFn: () => codebookApi.listExpertise(),
  });

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (profile) {
      const expertises = profile.expertises ?? [];
      setExpertiseIds(expertises.map((expertise) => expertise.id));
      setPrimaryIds(profile.primaryExpertiseIds ?? []);
      setCustomLabelById(
        Object.fromEntries(expertises.map((expertise) => [expertise.id, expertise.name])),
      );
    }
  }, [profile]);

  const optionLabels = useMemo(() => {
    const base = options.map((option) => ({
      id: option.id,
      name: t(`projects.expertise.options.${option.id}`, {
        defaultValue: option.name ?? option.id,
      }),
    }));
    const custom = expertiseIds
      .filter((id) => !base.some((option) => option.id === id))
      .map((id) => ({ id, name: customLabelById[id] ?? id }));
    return [...base, ...custom];
  }, [options, expertiseIds, customLabelById, t]);

  const mutation = useMutation({
    mutationFn: (payload: UpdateExpertisesDto) => profileApi.updateExpertises(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['me', 'expertises'] });
      queryClient.invalidateQueries({ queryKey: ['codebook', 'expertises'] });
      setShowSuccess(true);
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
      hideTimeoutRef.current = window.setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    },
  });

  const onChangeExpertises = (nextIds: string[]) => {
    setExpertiseIds(nextIds);
    setPrimaryIds((prev) => prev.filter((id) => nextIds.includes(id)));
    setCustomLabelById((prev) => ({
      ...prev,
      ...Object.fromEntries(
        nextIds.map((id) => [
          id,
          optionLabels.find((option) => option.id === id)?.name ?? prev[id] ?? id,
        ]),
      ),
    }));
  };

  const onCreateOption = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return null;
    const existing = optionLabels.find(
      (option) =>
        option.id === trimmed || option.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (existing) {
      if (!expertiseIds.includes(existing.id)) {
        onChangeExpertises([...expertiseIds, existing.id]);
      }
      return null;
    }
    setCustomLabelById((prev) => ({ ...prev, [trimmed]: trimmed }));
    return { id: trimmed, name: trimmed };
  };

  const togglePrimary = (id: string, checked: boolean) => {
    if (!expertiseIds.includes(id)) return;
    setPrimaryIds((prev) =>
      checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id),
    );
  };

  const onSave = useCallback(() => {
    mutation.mutate({
      expertiseIds,
      primaryExpertiseIds: primaryIds,
    });
  }, [expertiseIds, primaryIds, mutation]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('profile.expertises.title')}
          <SuccessIndicator visible={showSuccess} />
        </CardTitle>
        <CardDescription>{t('profile.expertises.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {loadingProfile || loadingOptions ? (
          <div>{t('generic.loading')}</div>
        ) : (
          <div className="space-y-6">
            <MultiSelectTags
              label={t('profile.expertises.label')}
              value={expertiseIds}
              onChange={onChangeExpertises}
              options={optionLabels}
              placeholder={t('profile.expertises.placeholder')}
              filterable
              allowCreate
              onCreateOption={onCreateOption}
              createOptionText={(label) => t('profile.expertises.createOption', { label })}
              searchPlaceholder={t('profile.multiSelect.searchPlaceholder')}
              noResultsText={t('profile.multiSelect.noResults')}
            />
            {expertiseIds.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('profile.expertises.primarySection')}</Label>
                <p className="text-sm text-muted-foreground">{t('profile.expertises.primaryHint')}</p>
                <div className="flex flex-col gap-2">
                  {expertiseIds.map((id) => {
                    const name = optionLabels.find((o) => o.id === id)?.name ?? id;
                    return (
                      <label key={id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={primaryIds.includes(id)}
                          onCheckedChange={(c) => togglePrimary(id, c === true)}
                        />
                        <span>{name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <Button type="button" onClick={onSave} disabled={mutation.isPending}>
              {mutation.isPending ? t('generic.saving') : t('generic.save')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
